import { useTranslation } from 'react-i18next'
import { Link, router } from '@inertiajs/react'
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Badge } from '../../../components/ui/badge'
import { Progress } from '../../../components/ui/progress'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select'

interface HealthCheckEntry {
  id: number
  status: 'healthy' | 'degraded' | 'unhealthy' | 'unreachable'
  memory_percent_used: number | null
  cpu_load_percent: number | null
  disk_percent_used: number | null
  pilot_reachable: boolean | null
  pilot_response_ms: number | null
  ec2_state: string | null
  error_category: string | null
  error_message: string | null
  action_taken: string | null
  created_at: string
  dev_instance: {
    id: number
    name: string
    runtime_state: string
    project_name: string | null
  }
}

interface HealthStats {
  total_checked: number
  healthy: number
  degraded: number
  unhealthy: number
  unreachable: number
  last_check_at: string | null
}

interface Pagination {
  count: number
  page: number
  items: number
  pages: number
  next: number | null
  prev: number | null
}

interface Filters {
  status: string | null
  sort: string | null
  dir: string
}

interface Props {
  stats: HealthStats
  problematic: HealthCheckEntry[]
  recent_actions: HealthCheckEntry[]
  all_checks: HealthCheckEntry[]
  pagy: Pagination
  filters: Filters
  auth: {
    user: { id: number; name: string; email: string; admin: boolean; super_admin?: boolean }
  }
}

const statusVariant: Record<string, 'success' | 'warning' | 'destructive' | 'secondary'> = {
  healthy: 'success',
  degraded: 'warning',
  unhealthy: 'destructive',
  unreachable: 'secondary',
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffSec < 60) return 'just now'
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`
  return `${Math.floor(diffSec / 86400)}d ago`
}

function formatPct(value: number | null): string {
  if (value == null) return '-'
  return `${value.toFixed(1)}%`
}

function pctColor(value: number | null): string {
  if (value == null) return 'text-muted-foreground'
  if (value > 90) return 'text-red-600 dark:text-red-400'
  if (value > 70) return 'text-amber-600 dark:text-amber-400'
  return 'text-green-600 dark:text-green-400'
}

function progressColor(value: number | null): string {
  if (value == null) return '[&>div]:bg-muted-foreground'
  if (value > 90) return '[&>div]:bg-red-500'
  if (value > 70) return '[&>div]:bg-amber-500'
  return '[&>div]:bg-green-500'
}

function buildUrl(params: Record<string, string | undefined>) {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v) p.set(k, v)
  }
  const qs = p.toString()
  return `/admin/dev_instances/health${qs ? `?${qs}` : ''}`
}

function Health({ stats, problematic, recent_actions, all_checks, pagy, filters }: Props) {
  const { t } = useTranslation('admin')

  const currentStatus = filters.status || undefined
  const sort = filters.sort || undefined
  const dir = filters.dir || 'asc'

  const handleStatusChange = (value: string) => {
    router.get(buildUrl({
      status: value !== 'all' ? value : undefined,
      sort,
      dir: sort ? dir : undefined,
    }), {}, { preserveState: true })
  }

  const handleSort = (field: string) => {
    const isActive = sort === field
    const nextDir = isActive && dir === 'asc' ? 'desc' : 'asc'
    router.get(buildUrl({
      status: currentStatus,
      sort: field,
      dir: nextDir,
    }), {}, { preserveState: true })
  }

  const buildPageUrl = (page: number) =>
    buildUrl({ page: String(page), status: currentStatus, sort, dir: sort ? dir : undefined })

  const SortIcon = ({ field }: { field: string }) => {
    if (sort !== field) return <ArrowUpDown className="size-3 opacity-40" />
    return dir === 'asc' ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />
  }

  return (
    <div className="flex flex-1 flex-col">
      <AppHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: t('healthChecks.overview.title') },
        ]}
      />
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">

          {/* Fleet Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="shadow-none">
              <CardHeader className="pb-2">
                <CardDescription>{t('healthChecks.stats.totalChecked')}</CardDescription>
                <CardTitle className="text-3xl">{stats.total_checked}</CardTitle>
              </CardHeader>
              {stats.last_check_at && (
                <CardContent className="pt-0">
                  <p className="text-xs text-muted-foreground">
                    {t('healthChecks.stats.lastCheckAt', { time: formatRelativeTime(stats.last_check_at) })}
                  </p>
                </CardContent>
              )}
            </Card>

            <Card className="shadow-none border-green-200 dark:border-green-900 bg-green-50/50 dark:bg-green-950/20">
              <CardHeader className="pb-2">
                <CardDescription>{t('healthChecks.stats.healthy')}</CardDescription>
                <CardTitle className="text-3xl text-green-700 dark:text-green-400">{stats.healthy}</CardTitle>
              </CardHeader>
            </Card>

            <Card className="shadow-none border-amber-200 dark:border-amber-900 bg-amber-50/50 dark:bg-amber-950/20">
              <CardHeader className="pb-2">
                <CardDescription>{t('healthChecks.stats.degraded')}</CardDescription>
                <CardTitle className="text-3xl text-amber-700 dark:text-amber-400">{stats.degraded}</CardTitle>
              </CardHeader>
            </Card>

            <Card className="shadow-none border-red-200 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20">
              <CardHeader className="pb-2">
                <CardDescription>{t('healthChecks.stats.unhealthy')}</CardDescription>
                <CardTitle className="text-3xl text-red-700 dark:text-red-400">{stats.unhealthy}</CardTitle>
              </CardHeader>
            </Card>

            <Card className="shadow-none">
              <CardHeader className="pb-2">
                <CardDescription>{t('healthChecks.stats.unreachable')}</CardDescription>
                <CardTitle className="text-3xl text-muted-foreground">{stats.unreachable}</CardTitle>
              </CardHeader>
            </Card>
          </div>

          {/* Problematic Instances */}
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>{t('healthChecks.problematic.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              {problematic.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('healthChecks.problematic.empty')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.instance')}</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.project')}</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.status')}</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.errorCategory')}</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.errorMessage')}</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.lastChecked')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {problematic.map(hc => (
                        <tr key={hc.id} className="hover:bg-muted transition-colors">
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <Link href={`/admin/dev_instances/${hc.dev_instance.id}`} className="text-sm text-blue-600 hover:text-blue-900 font-mono">
                              {hc.dev_instance.name}
                            </Link>
                          </td>
                          <td className="px-3 py-1.5 text-sm text-muted-foreground">{hc.dev_instance.project_name || '-'}</td>
                          <td className="px-3 py-1.5">
                            <Badge variant={statusVariant[hc.status]} className="text-xs">{t(`healthChecks.status.${hc.status}`)}</Badge>
                          </td>
                          <td className="px-3 py-1.5 text-sm font-mono text-muted-foreground">{hc.error_category || '-'}</td>
                          <td className="px-3 py-1.5 text-sm text-muted-foreground max-w-xs truncate">{hc.error_message || '-'}</td>
                          <td className="px-3 py-1.5 text-sm text-muted-foreground whitespace-nowrap">{formatRelativeTime(hc.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Remediation Actions */}
          {recent_actions.length > 0 && (
            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>{t('healthChecks.actions.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.instance')}</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.action')}</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.status')}</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.lastChecked')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {recent_actions.map(hc => (
                        <tr key={hc.id} className="hover:bg-muted transition-colors">
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <Link href={`/admin/dev_instances/${hc.dev_instance.id}`} className="text-sm text-blue-600 hover:text-blue-900 font-mono">
                              {hc.dev_instance.name}
                            </Link>
                          </td>
                          <td className="px-3 py-1.5 text-sm">{hc.action_taken}</td>
                          <td className="px-3 py-1.5">
                            <Badge variant={statusVariant[hc.status]} className="text-xs">{t(`healthChecks.status.${hc.status}`)}</Badge>
                          </td>
                          <td className="px-3 py-1.5 text-sm text-muted-foreground whitespace-nowrap">{formatRelativeTime(hc.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* All Instances Table */}
          <Card className="shadow-none">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t('healthChecks.overview.description')}</CardTitle>
              <Select value={currentStatus || 'all'} onValueChange={handleStatusChange}>
                <SelectTrigger className="h-7 text-xs w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('healthChecks.filters.allStatuses')}</SelectItem>
                  <SelectItem value="healthy">{t('healthChecks.status.healthy')}</SelectItem>
                  <SelectItem value="degraded">{t('healthChecks.status.degraded')}</SelectItem>
                  <SelectItem value="unhealthy">{t('healthChecks.status.unhealthy')}</SelectItem>
                  <SelectItem value="unreachable">{t('healthChecks.status.unreachable')}</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.instance')}</th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.project')}</th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.status')}</th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer select-none" onClick={() => handleSort('memory_percent_used')}>
                        <span className="inline-flex items-center gap-1">{t('healthChecks.table.memory')} <SortIcon field="memory_percent_used" /></span>
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer select-none" onClick={() => handleSort('cpu_load_percent')}>
                        <span className="inline-flex items-center gap-1">{t('healthChecks.table.cpu')} <SortIcon field="cpu_load_percent" /></span>
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer select-none" onClick={() => handleSort('disk_percent_used')}>
                        <span className="inline-flex items-center gap-1">{t('healthChecks.table.disk')} <SortIcon field="disk_percent_used" /></span>
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer select-none" onClick={() => handleSort('pilot_response_ms')}>
                        <span className="inline-flex items-center gap-1">{t('healthChecks.table.pilotMs')} <SortIcon field="pilot_response_ms" /></span>
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.lastChecked')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {all_checks.map(hc => (
                      <tr key={hc.id} className="hover:bg-muted transition-colors">
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          <Link href={`/admin/dev_instances/${hc.dev_instance.id}`} className="text-sm text-blue-600 hover:text-blue-900 font-mono">
                            {hc.dev_instance.name}
                          </Link>
                        </td>
                        <td className="px-3 py-1.5 text-sm text-muted-foreground">{hc.dev_instance.project_name || '-'}</td>
                        <td className="px-3 py-1.5">
                          <Badge variant={statusVariant[hc.status]} className="text-xs">{t(`healthChecks.status.${hc.status}`)}</Badge>
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="flex items-center gap-2 min-w-[100px]">
                            <Progress value={hc.memory_percent_used ?? 0} className={`h-1.5 flex-1 ${progressColor(hc.memory_percent_used)}`} />
                            <span className={`text-xs font-mono ${pctColor(hc.memory_percent_used)}`}>{formatPct(hc.memory_percent_used)}</span>
                          </div>
                        </td>
                        <td className="px-3 py-1.5">
                          <span className={`text-xs font-mono ${pctColor(hc.cpu_load_percent)}`}>{formatPct(hc.cpu_load_percent)}</span>
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="flex items-center gap-2 min-w-[100px]">
                            <Progress value={hc.disk_percent_used ?? 0} className={`h-1.5 flex-1 ${progressColor(hc.disk_percent_used)}`} />
                            <span className={`text-xs font-mono ${pctColor(hc.disk_percent_used)}`}>{formatPct(hc.disk_percent_used)}</span>
                          </div>
                        </td>
                        <td className="px-3 py-1.5 text-xs font-mono text-muted-foreground">
                          {hc.pilot_reachable ? (
                            <span className="text-green-600 dark:text-green-400">{hc.pilot_response_ms ?? '-'}ms</span>
                          ) : (
                            <span className="text-red-600 dark:text-red-400">down</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-sm text-muted-foreground whitespace-nowrap">{formatRelativeTime(hc.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagy.pages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Page {pagy.page} of {pagy.pages} ({pagy.count} total)
                  </p>
                  <div className="flex gap-2">
                    {pagy.prev && (
                      <Link href={buildPageUrl(pagy.prev)} className="text-sm text-blue-600 hover:text-blue-900">
                        Previous
                      </Link>
                    )}
                    {pagy.next && (
                      <Link href={buildPageUrl(pagy.next)} className="text-sm text-blue-600 hover:text-blue-900">
                        Next
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  )
}

Health.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Health
